/**
 * useSyncFreshness - single source of freshness data for an inspector FP.
 *
 * Wraps GET /inspector/{fpCode}/data/sync-status and exposes the worst
 * tier + oldest age across the FP's connected systems. Skimmer is excluded
 * per project_skimmer_not_active.
 *
 * Invalidates on the `sync:completed` custom window event - the existing
 * SyncStatusBarWrapper in InspectorLayout (the single per-page owner of
 * useActiveSyncStatus) dispatches it on completion. That keeps us off a
 * second WebSocket + poll loop while still refreshing freshness the moment
 * a sync finishes.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiClient } from '../services/api'
import {
  getFreshnessTier,
  getOldestAge,
  getWorstTier,
  type Tier,
} from '../utils/syncFreshness'

export interface FreshnessSystem {
  system: string
  status: string
  last_sync: string | null
  is_active: boolean
}

interface UseSyncFreshnessReturn {
  systems: FreshnessSystem[]
  worstTier: Tier
  oldestAge: string | null
  loading: boolean
  refetch: () => Promise<void>
}

export const SYNC_COMPLETED_EVENT = 'evop:sync-completed'

const FRESHNESS_SYSTEMS = ['lightspeed', 'xero'] as const

// Per-FP sessionStorage cache of the last-known systems response. Lets a fresh
// mount / hard reload paint the freshness banner in the same pass as the page
// content (stale-while-revalidate) instead of inserting it late and shifting
// everything down. Tiers are recomputed from `last_sync` at render, so a stale
// cache can only read *more* stale, never falsely fresh.
const freshnessCacheKey = (fpCode: string) =>
  `evop_sync_freshness_${fpCode.toLowerCase()}`

const readFreshnessCache = (fpCode: string): FreshnessSystem[] | null => {
  try {
    const raw = sessionStorage.getItem(freshnessCacheKey(fpCode))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as FreshnessSystem[]) : null
  } catch {
    return null
  }
}

const writeFreshnessCache = (fpCode: string, systems: FreshnessSystem[]) => {
  try {
    sessionStorage.setItem(freshnessCacheKey(fpCode), JSON.stringify(systems))
  } catch {}
}

export const useSyncFreshness = (fpCode: string): UseSyncFreshnessReturn => {
  const [systems, setSystems] = useState<FreshnessSystem[]>(
    () => readFreshnessCache(fpCode) ?? [],
  )
  const [loading, setLoading] = useState(() => readFreshnessCache(fpCode) === null)
  const mountedRef = useRef(true)
  const fpRef = useRef(fpCode)

  useEffect(() => () => {
    mountedRef.current = false
  }, [])

  const fetchFreshness = useCallback(async () => {
    try {
      const response = await apiClient.get(`/inspector/${fpCode}/data/sync-status`)
      const next: FreshnessSystem[] = response.data?.systems || []
      writeFreshnessCache(fpCode, next)
      if (mountedRef.current) setSystems(next)
    } catch (err) {
      // Keep the last-known value (cached seed or prior fetch) on a transient
      // failure rather than clearing to empty - dropping to "fresh" here would
      // make the banner vanish and shift content back up.
      console.error('Error fetching sync freshness:', err)
    } finally {
      if (mountedRef.current) setLoading(false)
    }
  }, [fpCode])

  useEffect(() => {
    // On a genuine FP switch (the hook now stays mounted across switches),
    // reseed from the new FP's cache before revalidating so we don't flash an
    // empty banner. First mount is already seeded by the lazy initializers.
    if (fpRef.current !== fpCode) {
      const cached = readFreshnessCache(fpCode)
      setSystems(cached ?? [])
      setLoading(cached === null)
      fpRef.current = fpCode
    }
    fetchFreshness()
  }, [fetchFreshness, fpCode])

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ fpCode?: string }>).detail
      if (!detail?.fpCode || detail.fpCode.toLowerCase() === fpCode.toLowerCase()) {
        fetchFreshness()
      }
    }
    window.addEventListener(SYNC_COMPLETED_EVENT, handler)
    return () => window.removeEventListener(SYNC_COMPLETED_EVENT, handler)
  }, [fpCode, fetchFreshness])

  const { worstTier, oldestAge } = useMemo(() => {
    const eligible = systems.filter((s) =>
      FRESHNESS_SYSTEMS.includes(s.system.toLowerCase() as (typeof FRESHNESS_SYSTEMS)[number])
    )
    if (eligible.length === 0) {
      return { worstTier: 'fresh' as Tier, oldestAge: null }
    }
    const tiers = eligible.map((s) => getFreshnessTier(s.last_sync))
    const ages = eligible.map((s) => s.last_sync)
    return {
      worstTier: getWorstTier(tiers),
      oldestAge: getOldestAge(ages),
    }
  }, [systems])

  return {
    systems,
    worstTier,
    oldestAge,
    loading,
    refetch: fetchFreshness,
  }
}

export default useSyncFreshness
